/**
 * 多版本菜单 B6（老扫码站 U-B6-2）：菜单变了之后对购物车做的事（用到 store / 接口的那一半；判断都在 `menuChanged.js` / `cartMarks.js`）。
 *
 * 1. 下单被拒（拦截器）：带 id 的售罄信封 ⇒ 按 id 标出购物车行（`markSoldOutLines`），标记带来历（回落 `fallback` / 普通 `order`）。
 *    只认 store 此刻的门店 + 菜单发出去的那一单（`rejectionIsCurrent`，review 第 1 轮 F3）；店家强塞的行不标（F4）。
 * 2. 本地核对（owner D97）：一次菜单解析得到的 menuId ≠ 建车菜单（`cartMenuId`，U-B6-1 记下的）、车里有这家店的行 ⇒
 *    先撤掉这份菜单上**有**的本地核对标记（`menu`），再把不在上面的行标出来、给一句提示，然后把建车菜单记成新菜单（review 第 1 轮 F2）。**从不清车。**
 *    - venue-home `getData`：商品表已在手上 ⇒ `checkCartAgainstMenu`；
 *    - 购物车页进店（store 里的菜单 ≠ 建车菜单：跨会话恢复出来的车、绕过首页的深链、商品页 / 推荐的静默重新解析）：
 *      手上没有商品表 ⇒ `recheckCartWithMenu` 取**一次** get-product-list（带这份 menuId）再核对。这是后台读（顾客什么都没点），失败不弹提示
 *      （review 第 2 轮 G7，`getProductListQuietly`）；后端回了话但读不出 / 读到空的那份菜单，这次打开网页里不再重试（F6），断网 / 超时 / 5xx 不记、下次再试。
 * 3. 「菜单刚更新」（owner D96 / D2 = A）：拦截器在弹窗**之前**调 `reviewAfterMenuUpdate`：按被拒那一单自己的 context 重新解析一次菜单
 *    （= 购物车此刻的用餐方式，owner B6-Q7 = A），取一次新菜单的商品表，撤掉这次新加的、新菜单上能下单的回落标记，再做 2。**不自动重新下单。**
 * 2、3 两处读商品表与 venue-home `_postProduct` 同一个写法：外卖系统定了预订时间就带 `preOrderDate`（review 第 2 轮 G9，status 按这一单的日子算）。
 */
import dayjs from 'dayjs'
import { getProductList, getProductListQuietly } from '@/api/product'
import { ORDER_OUTCOME_UNKNOWN, addOrderFailureOutcome } from '../orderPlaced'
import { requestBodyOf } from './menuRead'
import { reresolveMenu } from './reresolveMenu'
import { cartLinesOf, isForcedOnlyId, mergeExpireMarks, newMarkIds, reevaluateMenuMarks, unmarkOrderableFallback } from './cartMarks'
import { linesOffMenu, menuProductIds, orderableProductIds, soldOutMarks } from './menuChanged'

/**
 * 读这份菜单商品表的请求体（review 第 2 轮 G9）：与 venue-home `_postProduct` 同一个写法 —— 外卖系统、这家店的配送信息里定了预订时间 ⇒
 * 带 `preOrderDate`（`YYYY-MM-DD`），后端按那一天算库存 / status（不带就按今天：明天的单会拿今天的库存撤标 / 留标）。
 */
function productListRequest(state, venueId, menuId) {
  const data = { venueId }
  const deliveryInfoByVenue = state.contactDetails && state.contactDetails.deliveryInfo
  const deliveryInfo = (deliveryInfoByVenue && deliveryInfoByVenue[venueId]) || {}
  if (state.app && state.app.isOnline && deliveryInfo.preOrderTimeSettings) data.preOrderDate = dayjs(deliveryInfo.preOrderTimeSettings).format('YYYY-MM-DD')
  return { ...data, menuId }
}

/**
 * 读失败是不是「没拿到后端的回答」：断网 / 超时（拦截器网络分支 reject 的 undefined）、没有响应或 5xx、代码自己抛的 ——
 * 与下单那边同一个判据（`utils/orderPlaced.js addOrderFailureOutcome` 的「不知道」）。这种不记进下面的「读不出」名单（review 第 2 轮 G7）。
 */
const isTransportFailure = (error) => addOrderFailureOutcome(error) === ORDER_OUTCOME_UNKNOWN

/**
 * 拒单：按信封里的 id 标出购物车行（与已有标记合并，已有标记的 id 原样留着）；名字数组 / 没有 id ⇒ store 一个字不动。
 * 只有店家强塞行（`disableOperation`，顾客删不掉）的商品不标（F4）：标了下单按钮就永远灰着，那几件只弹今天的提示。
 * @returns {String[]} 这次**新加上**的 id（之后「菜单刚更新」只撤这些，不会把别人标的连带撤掉）
 */
export function markSoldOutLines(store, soldOut) {
  const state = store.state
  const marks = soldOutMarks(soldOut).filter(mark => !isForcedOnlyId(state.productList, state.venueId, mark.id))
  const added = newMarkIds(state.expireProductList, marks)
  if (added.length) store.commit('saveExpireProductList', mergeExpireMarks(state.expireProductList, marks))
  return added
}

/**
 * F3：这条拒单是不是 store 此刻的门店 + 菜单发出去的。不是（迟到的拒单：顾客已经换了店 / 菜单、重建了车）⇒ 不标、不走「菜单刚更新」，
 * 交给拦截器照今天的通用提示 —— 不然一条迟到的拒单会把新车里能买的东西标灰、按钮锁死。
 * @param {Object} state root state
 * @param {String|Object} requestData 被拒那一单的请求体
 */
export function rejectionIsCurrent(state, requestData) {
  const body = requestBodyOf(requestData)
  if (!state || !body) return false
  const menuId = typeof body.menuId === 'string' ? body.menuId : ''
  return !!menuId && typeof body.venueId === 'string' && body.venueId === state.venueId && menuId === state.menuId
}

/**
 * 要不要本地核对：解析出了菜单、它就是 store 里此刻的菜单（迟到的解析不算）、和建车菜单不一样、车里有这家店的行。
 * 建车菜单是空的（U-B6-1 之前建的车 / 建车时还没解析出菜单 / 改了时间清掉了标红）也算不一样。
 */
export function needsMenuCheck(state, menuId) {
  if (!state || typeof menuId !== 'string' || !menuId) return false
  if (state.menuId !== menuId || state.cartMenuId === menuId) return false
  return cartLinesOf(state.productList, state.venueId).length > 0
}

/** 按一份菜单重判本地核对的标记（有变化才写 store）；返回不在这份菜单上的行 */
function applyMenuMarks(store, productIds) {
  const state = store.state
  const off = linesOffMenu(state.productList, state.venueId, productIds)
  const before = Array.isArray(state.expireProductList) ? state.expireProductList : []
  const next = reevaluateMenuMarks(before, productIds, off)
  if (next.length !== before.length || next.some((mark, index) => mark !== before[index])) store.commit('saveExpireProductList', next)
  return off
}

/**
 * 商品表已在手上时的本地核对（D97）。
 * @param {Object} store Vuex store
 * @param {Object} where
 * @param {String} where.menuId 这次解析出的菜单
 * @param {Array} where.productGroups 这份菜单的 get-product-list `productGroup`
 * @param {Function} [where.notify] 有不在菜单上的行时调一次（venue-home / 购物车页给一句提示）
 * @returns {String[]} 不在这份菜单上的商品 id
 */
export function checkCartAgainstMenu(store, { menuId, productGroups, notify } = {}) {
  const state = store.state
  if (!needsMenuCheck(state, menuId)) return []
  const productIds = menuProductIds(productGroups)
  // 商品表读不出 / 是空的：说不清，不标，也不改建车菜单（下一次解析再来）
  if (!productIds) return []
  const off = applyMenuMarks(store, productIds)
  store.commit('saveCartMenuId', menuId)
  if (off.length && typeof notify === 'function') notify()
  return off
}

/**
 * 后端回了话但读不出 / 读到空商品表的那几份菜单（review 第 1 轮 F6）：这次打开网页（模块活着）期间不再为它们重取 —— 不然每进一次购物车页都重读一整份商品表。
 * 换了菜单照常取；重新打开网页就忘掉。读成功不记（成功之后建车菜单就是它，本来就不会再取）。
 * 断网 / 超时 / 5xx（没拿到后端的回答）不记（review 第 2 轮 G7）：一次网络抖动不能让这次打开网页里的核对整个失效。
 */
const unreadableMenuChecks = new Set()

/** 忘掉上面那份记录（测试用；生产里随页面重新加载自然清空） */
export function forgetUnreadableMenuChecks() {
  unreadableMenuChecks.clear()
}

/**
 * 手上没有商品表时（购物车页进店）：取一次 store 里这份菜单的 get-product-list（带 menuId，门店用 store 里的 UUID），再核对。最多一个请求。
 * 这是后台读、顾客什么都没点：走静默的读法（review 第 2 轮 G7，`getProductListQuietly`，失败不弹「Network error」也不弹后端那句）。
 * 读失败 / 读到空的 ⇒ 不抛、不标、不改建车菜单；后端回了话（含「菜单不在用」）读不出 / 是空的 ⇒ 记下这份菜单这次不再重试，
 * 没拿到后端的回答（断网 / 超时 / 5xx）⇒ 不记，下次进购物车再试。
 * 回来时 store 换了菜单 / 换了店 ⇒ 不核对：交给 checkCartAgainstMenu 里的 needsMenuCheck（它要求 store 此刻的菜单就是这一份；
 * 换店时 resetState 会把 menuId 清空），这里不再另判一次。
 */
export async function recheckCartWithMenu(store, { notify } = {}) {
  const state = store.state
  const menuId = state.menuId
  const venueId = state.venueId
  if (!needsMenuCheck(state, menuId)) return []
  if (unreadableMenuChecks.has(menuId)) return []
  let res
  try {
    res = await getProductListQuietly(productListRequest(state, venueId, menuId))
  } catch (error) {
    console.log(error)
    if (!isTransportFailure(error)) unreadableMenuChecks.add(menuId)
    return []
  }
  const productGroups = res && res.productGroup
  if (!menuProductIds(productGroups)) {
    unreadableMenuChecks.add(menuId)
    return []
  }
  return checkCartAgainstMenu(store, { menuId, productGroups, notify })
}

const NOT_REVIEWED = Object.freeze({ reviewed: false })

/**
 * 「菜单刚更新」（owner D96 / D2 = A；拦截器在弹窗**之前**调，加载圈一直开着）：
 * 1. 按被拒那一单的 context 重新解析一次菜单 C（`reresolveMenu`，门店 / 合单守卫照旧；失败放回原菜单）；
 * 2. C 非空、不是被拒那一单自己的菜单、store 仍是这家店且此刻的菜单就是 C ⇒ 取**一次** C 的 get-product-list（C 就是建车菜单也取）；
 * 3. 回来时再确认一遍 store 没换店 / 没换菜单，表读得出、不是空的 ⇒
 *    撤掉 `fallbackIds`（这次拒单新加上的回落标记）里 C 卖、而且在 C 上能下单（status 1）的；再用同一份表做 D97 重判；建车菜单 = C。
 * 任何一步不成立 ⇒ 标记一条不动（宁可多留一个让顾客删的，也不放行后端刚拒的）；C 就是被拒那份时 console.warn（后端下单与 get-menu 对同一个 context 意见不一）。
 * @param {Object} store Vuex store
 * @param {String|Object} requestData 被拒那一单的请求体（拦截器手上是 axios 序列化后的 JSON 串）
 * @param {Object} [options]
 * @param {String[]} [options.fallbackIds] 这次拒单新加上的回落标记（`markSoldOutLines` 的返回值）
 * @returns {Promise<{reviewed: Boolean, menuId?: String, off?: String[]}>} reviewed = 按 C 重判过了（弹窗按购物车此刻的标记列名字）
 */
export async function reviewAfterMenuUpdate(store, requestData, { fallbackIds = [] } = {}) {
  const body = requestBodyOf(requestData)
  if (!body || typeof body.venueId !== 'string' || !body.venueId) return NOT_REVIEWED
  const rejectedMenuId = typeof body.menuId === 'string' ? body.menuId : ''
  const context = body.context && typeof body.context === 'object' && !Array.isArray(body.context) ? body.context : undefined
  const venueId = store.state.venueId
  const menuId = await reresolveMenu(store, { venueId: body.venueId, context })
  if (!menuId) return NOT_REVIEWED
  if (menuId === rejectedMenuId) {
    console.warn('[multi-menu] 重新解析出的还是被拒那一单的菜单（下单与 get-menu 对同一个 context 意见不一），标记全部留着', menuId)
    return NOT_REVIEWED
  }
  const current = () => store.state.venueId === venueId && store.state.menuId === menuId
  if (!current()) return NOT_REVIEWED
  let res
  try {
    res = await getProductList(productListRequest(store.state, venueId, menuId))
  } catch (error) {
    console.log(error)
    return NOT_REVIEWED
  }
  if (!current()) return NOT_REVIEWED
  const productGroups = res && res.productGroup
  const productIds = menuProductIds(productGroups)
  if (!productIds) return NOT_REVIEWED
  const before = Array.isArray(store.state.expireProductList) ? store.state.expireProductList : []
  const unmarked = unmarkOrderableFallback(before, fallbackIds, orderableProductIds(productGroups))
  if (unmarked.length !== before.length) store.commit('saveExpireProductList', unmarked)
  const off = applyMenuMarks(store, productIds)
  store.commit('saveCartMenuId', menuId)
  return { reviewed: true, menuId, off }
}
